import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NewsItem, requestNewsItemProcessing, saveNewsItemForLater } from '../../services/newsItemService';
import { Topic, addTopic } from '../../services/topicService';
import { UserService } from '../../services/userService';
import './NewsItemComponent.css';

interface NewsItemComponentProps {
  newsItem: NewsItem;
  onTopicClick?: (topic: Topic) => void;
  onMarkAsRead?: (newsItemId: string) => void;
  onHide?: (newsItemId: string) => void;
  onSaveForLater?: (newsItemId: string) => void;
  user?: any; // Add user prop to access user ID for topic subscriptions
  showSaveButton?: boolean; // Whether to show the save button
}

const NewsItemComponent: React.FC<NewsItemComponentProps> = ({ newsItem, onTopicClick, onMarkAsRead, onHide, onSaveForLater, user, showSaveButton = true }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showExpandedSummary, setShowExpandedSummary] = useState(false);
  const [isRequestingProcessing, setIsRequestingProcessing] = useState(false);
  const [tagFeedback, setTagFeedback] = useState<{message: string, type: 'success' | 'error' | 'info'} | null>(null);
  const [forceUpdate, setForceUpdate] = useState(0); // Force re-render for timeout checks
  const feedbackTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const requestingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Check if processing has been going on for more than 5 minutes
  const isProcessingTimedOut = useCallback((): boolean => {
    if (newsItem.status !== 'PROCESSING' || !newsItem.processingLastAttemptedAt) {
      return false;
    }
    
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000); // 5 minutes ago
    const processingStarted = new Date(newsItem.processingLastAttemptedAt);
    
    return processingStarted <= fiveMinutesAgo;
  }, [newsItem.status, newsItem.processingLastAttemptedAt]);

  // Reset requesting state when news item status changes
  useEffect(() => {
    if (newsItem.status === 'PROCESSING' || newsItem.status === 'PROCESSED') {
      setIsRequestingProcessing(false);
      // Clear any pending timeout since the status changed
      if (requestingTimeoutRef.current) {
        clearTimeout(requestingTimeoutRef.current);
        requestingTimeoutRef.current = null;
      }
    }
  }, [newsItem.status]);

  // Set up periodic check for processing timeout
  useEffect(() => {
    if (newsItem.status === 'PROCESSING' && newsItem.processingLastAttemptedAt) {
      // Check every 30 seconds if processing has timed out
      timeoutCheckIntervalRef.current = setInterval(() => {
        if (isProcessingTimedOut()) {
          // Force a re-render by updating the state
          setForceUpdate(prev => prev + 1);
        }
      }, 30000); // Check every 30 seconds

      return () => {
        if (timeoutCheckIntervalRef.current) {
          clearInterval(timeoutCheckIntervalRef.current);
          timeoutCheckIntervalRef.current = null;
        }
      };
    } else {
      // Clear interval if not processing
      if (timeoutCheckIntervalRef.current) {
        clearInterval(timeoutCheckIntervalRef.current);
        timeoutCheckIntervalRef.current = null;
      }
    }
  }, [newsItem.status, newsItem.processingLastAttemptedAt, forceUpdate, isProcessingTimedOut]);

  // Cleanup timeouts on component unmount
  useEffect(() => {
    return () => {
      if (feedbackTimeoutRef.current) {
        clearTimeout(feedbackTimeoutRef.current);
        feedbackTimeoutRef.current = null;
      }
      if (requestingTimeoutRef.current) {
        clearTimeout(requestingTimeoutRef.current);
        requestingTimeoutRef.current = null;
      }
      if (timeoutCheckIntervalRef.current) {
        clearInterval(timeoutCheckIntervalRef.current);
        timeoutCheckIntervalRef.current = null;
      }
    };
  }, []);

  // Helper function to format text as bullet points
  const formatAsBulletPoints = (text: string, showReadMore: boolean = false, onReadMore?: () => void): JSX.Element[] => {
    // Split sentences while preserving abbreviations like U.S., U.K., etc.
    const sentences = text.split(/(?<!\b[A-Z])\.\s+(?=[A-Z])/)
      .filter(sentence => sentence.trim().length > 0);
    
    const bulletPoints = sentences.map((sentence, index, array) => (
      <div key={index} className="bullet-point">
        {sentence.trim()}{index < array.length - 1 && sentence.trim() && !sentence.endsWith('.') ? '.' : ''}
      </div>
    ));

    // Add "View detailed summary" bullet point if requested
    if (showReadMore && onReadMore) {
      bulletPoints.push(
        <div key="read-more" className="bullet-point read-more-bullet" onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onReadMore();
        }}>
          <span className="read-more-text">View detailed summary</span>
        </div>
      );
    }

    return bulletPoints;
  };

  // Helper function to set feedback with timeout management
  const setTagFeedbackWithTimeout = (feedback: {message: string, type: 'success' | 'error' | 'info'} | null) => {
    // Clear any existing timeout
    if (feedbackTimeoutRef.current) {
      clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = null;
    }
    
    setTagFeedback(feedback);
    
    // Set new timeout if feedback is not null
    if (feedback) {
      feedbackTimeoutRef.current = setTimeout(() => {
        setTagFeedback(null);
        feedbackTimeoutRef.current = null;
      }, 3000);
    }
  };

  const formatDate = (date: Date) => {
    const day = date.getDate();
    const month = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date);
    const year = date.getFullYear();
    
    // Add ordinal suffix to day
    const getOrdinalSuffix = (day: number) => {
      if (day >= 11 && day <= 13) {
        return 'th';
      }
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${month} ${day}${getOrdinalSuffix(day)} ${year}`;
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Use actualUrl if available, otherwise fall back to original link
    const linkUrl = newsItem.actualUrl || newsItem.link;
    window.open(linkUrl, '_blank', 'noopener,noreferrer');
  };

  const handleSummaryToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const newIsExpanded = !isExpanded;
    setIsExpanded(newIsExpanded);
    
    // Reset to regular summary when minimizing
    if (!newIsExpanded) {
      setShowExpandedSummary(false);
    }
  };

  const handleRequestSummary = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!newsItem.id || isRequestingProcessing) {
      return;
    }
    
    setIsRequestingProcessing(true);
    
    // Set a timeout to reset the requesting state after 10 seconds
    if (requestingTimeoutRef.current) {
      clearTimeout(requestingTimeoutRef.current);
    }
    requestingTimeoutRef.current = setTimeout(() => {
      setIsRequestingProcessing(false);
      requestingTimeoutRef.current = null;
    }, 10000); // 10 second timeout
    
    try {
      await requestNewsItemProcessing(newsItem.id!);
      // Successfully requested processing
    } catch (error) {
      console.error('Error requesting summary for news item:', newsItem.id, error);
      // Reset the state if there was an error
      setIsRequestingProcessing(false);
      if (requestingTimeoutRef.current) {
        clearTimeout(requestingTimeoutRef.current);
        requestingTimeoutRef.current = null;
      }
    }
  };

  const handleTopicClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (onTopicClick) {
      // First check if this topic actually exists by trying to use it for filtering
      // The topicId from newsItem should correspond to an existing topic
      const topic: Topic = {
        id: newsItem.topicId,
        topicValue: newsItem.topicValue,
        createdAt: new Date(), // This will be properly loaded when needed
      };
      
      // If the topic exists (has a real ID), filter and scroll to top
      if (newsItem.topicId && newsItem.topicId.trim() !== '') {
        onTopicClick(topic);
        
        // Scroll to top after clicking the topic (for existing topics)
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      } else {
        // If no topicId, try to add the topic (this will return existing topic if it already exists)
        try {
          const result = await addTopic(newsItem.topicValue);
          
          if (result.success) {
            if (result.message.includes('already exists')) {
              // Topic already exists, so filter by it instead of showing feedback
              const existingTopic: Topic = {
                id: result.topicId, // Use the actual topic ID from the backend
                topicValue: newsItem.topicValue,
                createdAt: new Date(), // This will be properly loaded when needed
              };
              onTopicClick(existingTopic);
              
              // Scroll to top after clicking the topic
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            } else {
              // New topic was created, show success message
              setTagFeedbackWithTimeout({ message: `"${newsItem.topicValue}" added as new topic`, type: 'success' });
            }
          } else {
            setTagFeedbackWithTimeout({ message: `Failed to add "${newsItem.topicValue}" as topic`, type: 'error' });
          }
          
        } catch (error: any) {
          console.error('Error adding topic:', error);
          setTagFeedbackWithTimeout({ message: `Error adding "${newsItem.topicValue}" as topic`, type: 'error' });
        }
      }
    }
  };

  const handleMarkAsReadClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onMarkAsRead && newsItem.id) {
      // Use the onMarkAsRead callback which provides optimistic updates
      onMarkAsRead(newsItem.id);
    }
  };

  const handleSaveForLaterClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onSaveForLater && newsItem.id) {
      try {
        onSaveForLater(newsItem.id);
        await saveNewsItemForLater(newsItem.id);
      } catch (error) {
        console.error('Error saving for later:', error);
        // Note: Since we already hid the item optimistically, we might need to show it again on error
        // But for now, we'll just log the error
      }
    }
  };

  const handleDeleteClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onHide && newsItem.id) {
      onHide(newsItem.id);
    }
  };

  const handleTagClick = async (e: React.MouseEvent, tag: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (onTopicClick) {
      try {
        const result = await addTopic(tag, 'USER', user?.id);
        
        if (result.success) {
          if (result.message.includes('already exists')) {
            // Topic already exists, so filter by it and scroll to top
            const existingTopic: Topic = {
              id: result.topicId, // Use the actual topic ID from the backend
              topicValue: tag,
              createdAt: new Date(), // This will be properly loaded when needed
            };
            onTopicClick(existingTopic);
            
            // Scroll to top smoothly only when filtering by existing topic
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          } else {
            // New topic was created, show success message (don't scroll)
            setTagFeedbackWithTimeout({ message: `"${tag}" added as new topic`, type: 'success' });
          }
        } else {
          setTagFeedbackWithTimeout({ message: `Failed to add "${tag}" as topic`, type: 'error' });
        }
        
      } catch (error: any) {
        console.error('Error adding tag as topic:', error);
        setTagFeedbackWithTimeout({ message: `Error adding "${tag}" as topic`, type: 'error' });
      }
    }
  };

  const handleNewsItemClick = (e: React.MouseEvent) => {
    // Prevent any default selection behavior on the news item itself
    e.preventDefault();
    
    // Check if the click target is an interactive element or its child
    const target = e.target as HTMLElement;
    const isInteractiveElement = target.closest('button, a, .news-item-tag, .news-item-topic, .read-more-bullet');
    
    // If it's not an interactive element and the news item has a summary, toggle it
    if (!isInteractiveElement && 
        newsItem.status === 'PROCESSED' && 
        newsItem.summary && 
        !newsItem.isPaywalled) {
      setIsExpanded(!isExpanded);
      
      // Reset to regular summary when minimizing
      if (isExpanded) {
        setShowExpandedSummary(false);
      }
    }
  };

  return (
    <div 
      className={`news-item ${newsItem.status === 'PROCESSED' && newsItem.summary && !newsItem.isPaywalled ? 'has-summary' : ''}`}
      onClick={handleNewsItemClick}
      onMouseDown={(e) => e.preventDefault()}
      tabIndex={-1}
    >
      <div className="news-item-content">
        <div className="news-item-header">
          <div className="news-item-title-section">
            {/* Featured image thumbnail in its own container */}
            {newsItem.featuredImageUrl && (
              <div className="news-item-image-container">
                  <img 
                    src={newsItem.featuredImageUrl} 
                    alt="Featured" 
                    className="news-item-featured-thumb"
                    loading="lazy"
                  />
              </div>
            )}
            
            <div className="news-item-text-content">
              <h3 className="news-item-title">
                <a 
                  href={newsItem.actualUrl || newsItem.link} 
                  onClick={handleLinkClick}
                  className="news-item-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {newsItem.title}
                </a>
              </h3>

              {/* Display source and date on the same line */}
              {newsItem.source && (
                <p className="news-item-source">
                  {newsItem.source} - {formatDate(newsItem.publishedAt)}
                </p>
              )}
            </div>
          </div>
          
          <div className="news-item-header-actions">
            {/* Show Paywall indicator if article is paywalled */}
            {newsItem.status === 'PROCESSED' && newsItem.isPaywalled && (
              <span className="news-item-paywall-badge">
                paywall
              </span>
            )}
            
            {/* Conditional buttons based on status - only if NOT paywalled */}
            {newsItem.status === 'PROCESSED' && newsItem.summary && !newsItem.isPaywalled && (
              <button 
                className="summary-button"
                onClick={handleSummaryToggle}
                title={isExpanded ? "Hide summary" : "See summary"}
              >
                {isExpanded ? 'Minimize' : 'Summary'}
              </button>
            )}
            
            {/* Show Summarise button for READY_TO_PROCESS status or when PROCESSING has timed out */}
            {((newsItem.status === 'READY_TO_PROCESS') || (newsItem.status === 'PROCESSING' && isProcessingTimedOut())) && !isRequestingProcessing && (
              <button 
                className="request-summary-button"
                onClick={handleRequestSummary}
                title="Request AI summary"
              >
                Summarise
              </button>
            )}

            {/* Show processing indicator when actively processing and NOT timed out */}
            {((newsItem.status === 'PROCESSING' && !isProcessingTimedOut()) || isRequestingProcessing) && (
              <button 
                className="processing-indicator clickable-processing"
                onClick={handleRequestSummary}
                title="Re-request summary (click to retry if stuck)"
              >
                Summarising
              </button>
            )}
          </div>
        </div>

        {/* Display summary and tags if available, status is PROCESSED, expanded, and NOT paywalled */}
        {newsItem.status === 'PROCESSED' && newsItem.summary && isExpanded && !newsItem.isPaywalled && (
          <div className="news-item-summary-section">
            <div className="news-item-summary-content">
              <div className="news-item-summary">
                {/* Show extended summary if selected, otherwise show regular summary with "View detailed summary" */}
                {showExpandedSummary && newsItem.expandedSummary ? (
                  formatAsBulletPoints(newsItem.expandedSummary)
                ) : (
                  formatAsBulletPoints(
                    newsItem.summary,
                    !showExpandedSummary && !!newsItem.expandedSummary, // show "View detailed summary" if extended summary exists and not currently showing it
                    () => {
                      // Create a synthetic event for the handler
                      setShowExpandedSummary(true);
                    }
                  )
                )}
              </div>
            </div>
          </div>
        )}

        {/* Display tags if processed and expanded (but not paywall badge here since it's in header) */}
        {newsItem.status === 'PROCESSED' && newsItem.tags && isExpanded && !newsItem.isPaywalled && (
          <div className="news-item-tags">
            {newsItem.tags.map((tag, index) => (
              <span 
                key={index} 
                className="news-item-tag"
                onClick={(e) => handleTagClick(e, tag)}
                title={`Click to add "${tag}" as a topic`}
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Tag feedback message */}
        {tagFeedback && (
          <div className={`tag-feedback tag-feedback-${tagFeedback.type}`}>
            {tagFeedback.message}
          </div>
        )}
        
        <div className="news-item-bottom">
          <div className="news-item-meta">
            {onMarkAsRead && (
              <button 
                className="read-button news-item-read"
                onClick={handleMarkAsReadClick}
                title="Mark as read"
              >
                Read <i className="fas fa-check"></i>
              </button>
            )}
            {onMarkAsRead && showSaveButton && (
              <button 
                className="save-button news-item-save"
                onClick={handleSaveForLaterClick}
                title="Read later"
              >
                Save <i className="fas fa-bookmark"></i>
              </button>
            )}
            {UserService.isAdmin(user) && (
              <button 
                className="delete-button news-item-delete"
                onClick={handleDeleteClick}
                title="Hide news item"
              >
                <i className="fas fa-trash"></i>
              </button>
            )}
          </div>
          <div className="news-item-actions">
            <span 
              className={`news-item-topic ${onTopicClick ? 'clickable' : ''}`}
              onClick={handleTopicClick}
            >
              {newsItem.topicValue}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsItemComponent;